import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Client, ClientDetail, ClientInput, CLIENT_NAME_MAX } from '../../types';
import * as api from '../../services/api';

/**
 * /clients — the Client directory.
 *
 * A Client is the person/company/destination that RECEIVES leads out of a
 * delivery leg-node. The directory seeds itself implicitly — every delivery
 * node requires a client, and typing a new name in a node creates one — and
 * this page is where the rest of the record gets filled in.
 *
 * Clicking a row opens a detail modal with the full record, every destination
 * the client has ever been wired to (deleted ones included and marked), and a
 * link into its reporting drilldown.
 *
 * Two things behave differently from Sources, both downstream of the
 * assignment lock in migration 031:
 *   - A client that has taken a delivery cannot be deleted. Archive instead;
 *     its statistics are permanent.
 *   - Names are capped at 25 characters so they fit on a leg-node card.
 *
 * Account-isolated server-side: the API resolves the account from the JWT,
 * there is no caller-supplied tenant id.
 */

const EMPTY_INPUT: ClientInput = {
  name: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  notes: '',
};

function toInput(c: Client): ClientInput {
  return {
    name: c.name,
    contactName: c.contactName ?? '',
    contactEmail: c.contactEmail ?? '',
    contactPhone: c.contactPhone ?? '',
    addressLine1: c.addressLine1 ?? '',
    addressLine2: c.addressLine2 ?? '',
    city: c.city ?? '',
    state: c.state ?? '',
    postalCode: c.postalCode ?? '',
    country: c.country ?? '',
    notes: c.notes ?? '',
  };
}

export default function ClientsPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState<ClientDetail | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setClients(await api.getClients(showArchived));
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { void load(); }, [load]);

  async function openClient(id: string) {
    try {
      setSelected(await api.getClient(id));
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load client');
    }
  }

  const q = search.trim().toLowerCase();
  const visible = q
    ? clients.filter(c =>
        [c.name, c.contactName, c.contactEmail, c.contactPhone, c.city, c.state]
          .some(v => (v || '').toLowerCase().includes(q)))
    : clients;

  const cols = '1.2fr 1.1fr 1.4fr 1fr 120px 110px';

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Clients</h1>
          <p className="page-subtitle">
            The people and destinations that receive leads from your legs. One client can receive from several destinations across several data bodies.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>Add Client</button>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 300px', maxWidth: 360 }}>
          <input
            className="form-input"
            type="search"
            placeholder="Search clients…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: '#6b7280' }}>
          <input
            type="checkbox"
            checked={showArchived}
            onChange={e => setShowArchived(e.target.checked)}
          />
          Show archived
        </label>
      </div>

      {loading && <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>}

      {!loading && visible.length === 0 && (
        <div style={{ padding: 32, background: '#f9fafb', border: '1px dashed #e5e7eb', borderRadius: 8, color: '#6b7280', textAlign: 'center' }}>
          {clients.length === 0
            ? 'No clients yet. They get created automatically the first time you name one on a delivery node — or add one here.'
            : 'No clients match that search.'}
        </div>
      )}

      {!loading && visible.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: cols,
            gap: 12,
            padding: '10px 16px',
            background: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            fontSize: 11,
            fontWeight: 600,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}>
            <div>Client</div>
            <div>Contact</div>
            <div>Email</div>
            <div>Phone</div>
            <div style={{ textAlign: 'right' }}>Destinations</div>
            <div style={{ textAlign: 'right' }}>Reporting</div>
          </div>
          {visible.map((c, i) => (
            <div
              key={c.id}
              role="button"
              tabIndex={0}
              onClick={() => openClient(c.id)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); void openClient(c.id); } }}
              style={{
                display: 'grid',
                gridTemplateColumns: cols,
                gap: 12,
                padding: '13px 16px',
                borderTop: i === 0 ? 'none' : '1px solid #f3f4f6',
                alignItems: 'center',
                fontSize: 14,
                cursor: 'pointer',
                opacity: c.status === 'archived' ? 0.55 : 1,
              }}
            >
              <div style={{ fontWeight: 600, color: '#111827' }}>
                {c.name}
                {c.status === 'archived' && (
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: '#9ca3af' }}>archived</span>
                )}
              </div>
              <div style={{ color: c.contactName ? '#374151' : '#d1d5db' }}>{c.contactName || '—'}</div>
              <div style={{ color: c.contactEmail ? '#374151' : '#d1d5db', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.contactEmail || '—'}</div>
              <div style={{ color: c.contactPhone ? '#374151' : '#d1d5db' }}>{c.contactPhone || '—'}</div>
              <div style={{ textAlign: 'right', color: '#6b7280' }}>
                {c.nodeCount ?? 0}
                {/* The gap between live and all-time destinations is the
                    history the assignment ledger exists to keep, so it is
                    worth showing rather than hiding. */}
                {typeof c.totalNodeCount === 'number' && c.totalNodeCount > (c.nodeCount ?? 0) && (
                  <span style={{ color: '#9ca3af', fontSize: 12 }}> / {c.totalNodeCount} all time</span>
                )}
              </div>
              <div style={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                <Link
                  to={`/reports/clients/${c.id}`}
                  style={{ fontSize: 13, color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
                >
                  Stats →
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <ClientModal
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={async () => { setCreating(false); await load(); }}
        />
      )}

      {selected && (
        <ClientModal
          mode="edit"
          detail={selected}
          onClose={() => setSelected(null)}
          onSaved={async () => { setSelected(null); await load(); }}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------
// Detail / create modal
// -------------------------------------------------------------------
// One component for both because the fields are identical; `mode` only
// changes the title, the submit verb, and whether the destinations section
// and the archive/delete actions are shown.

function ClientModal({
  mode,
  detail,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  detail?: ClientDetail;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [form, setForm] = useState<ClientInput>(detail ? toInput(detail) : EMPTY_INPUT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const set = <K extends keyof ClientInput>(k: K, v: ClientInput[K]) =>
    setForm(prev => ({ ...prev, [k]: v }));

  const assignments = detail?.assignments ?? [];
  // A client that has delivered is permanent: deleting it would cascade away
  // the assignment ledger and orphan reporting the product promises to keep.
  const hasDeliveries = assignments.some(a => a.locked);

  async function save() {
    const name = form.name.trim();
    if (!name) { setError('Client name is required'); return; }
    if (name.length > CLIENT_NAME_MAX) {
      setError(`Name must be ${CLIENT_NAME_MAX} characters or fewer`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (mode === 'create') {await api.createClient(form);}
      else {await api.updateClient(detail!.id, form);}
      await onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function archive() {
    setSaving(true);
    setError('');
    try {
      await api.updateClient(detail!.id, { status: detail!.status === 'archived' ? 'active' : 'archived' });
      await onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to archive');
      setSaving(false);
    }
  }

  async function remove() {
    setSaving(true);
    setError('');
    try {
      await api.deleteClient(detail!.id);
      await onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to delete');
      setSaving(false);
    }
  }

  const nameLen = (form.name || '').trim().length;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 680, width: '100%' }}>
        <h2 className="modal-title">{mode === 'create' ? 'Add Client' : detail!.name}</h2>

        <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 4 }}>
          <div className="form-group">
            <label className="form-label">
              Client name
              <span style={{ marginLeft: 8, fontWeight: 400, color: nameLen > CLIENT_NAME_MAX ? '#dc2626' : '#9ca3af' }}>
                {nameLen}/{CLIENT_NAME_MAX}
              </span>
            </label>
            <input
              className="form-input"
              maxLength={CLIENT_NAME_MAX}
              value={form.name}
              onChange={e => set('name', e.target.value)}
              autoFocus
            />
            <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>
              Kept short so it reads cleanly on a leg-node card.
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Contact</label>
              <input className="form-input" value={form.contactName ?? ''} onChange={e => set('contactName', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Phone</label>
              <input className="form-input" value={form.contactPhone ?? ''} onChange={e => set('contactPhone', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.contactEmail ?? ''} onChange={e => set('contactEmail', e.target.value)} />
          </div>

          <div className="form-group">
            <label className="form-label">Address</label>
            <input
              className="form-input"
              placeholder="Street address"
              value={form.addressLine1 ?? ''}
              onChange={e => set('addressLine1', e.target.value)}
            />
          </div>
          <div className="form-group">
            <input
              className="form-input"
              placeholder="Suite, floor, etc. (optional)"
              value={form.addressLine2 ?? ''}
              onChange={e => set('addressLine2', e.target.value)}
            />
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 2 }}>
              <label className="form-label">City</label>
              <input className="form-input" value={form.city ?? ''} onChange={e => set('city', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">State</label>
              <input className="form-input" value={form.state ?? ''} onChange={e => set('state', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Postal Code</label>
              <input className="form-input" value={form.postalCode ?? ''} onChange={e => set('postalCode', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Country</label>
              <input className="form-input" value={form.country ?? ''} onChange={e => set('country', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-input"
              rows={4}
              placeholder="Payout, caps, accepted verticals, delivery format quirks, anything worth remembering."
              value={form.notes ?? ''}
              onChange={e => set('notes', e.target.value)}
              style={{ resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>

          {mode === 'edit' && (
            <div style={{ marginTop: 8, paddingTop: 16, borderTop: '1px solid #f3f4f6' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                Destinations ({assignments.length})
              </div>
              {assignments.length === 0 ? (
                <div style={{ fontSize: 13, color: '#9ca3af' }}>
                  No destinations wired to this client yet. Assign it on a delivery node inside a leg.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {assignments.map(a => (
                    <div
                      key={a.legNodeId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        fontSize: 13,
                        opacity: a.detachedAt ? 0.6 : 1,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.nodeName || '(unnamed destination)'}
                        </div>
                        <div style={{ fontSize: 12, color: '#9ca3af' }}>
                          {[a.dataBodyName, a.legName, (a.nodeType || '').replace(/_/g, ' ')]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {a.detachedAt ? (
                          // Deleted, but its numbers are still counted for this
                          // client. Saying so here is the whole promise.
                          <span style={{ fontSize: 11, color: '#9ca3af' }}>deleted · stats kept</span>
                        ) : a.locked ? (
                          <span style={{ fontSize: 11, color: '#047857', fontWeight: 600 }}>delivering</span>
                        ) : (
                          <span style={{ fontSize: 11, color: '#92400e' }}>not yet delivered</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ marginTop: 12 }}>
                <Link
                  to={`/reports/clients/${detail!.id}`}
                  style={{ fontSize: 13, color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
                >
                  View reporting for this client →
                </Link>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 13, marginTop: 12 }}>
            {error}
          </div>
        )}

        {confirmDelete && (
          <div style={{ padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, color: '#92400e', fontSize: 13, marginTop: 12 }}>
            Delete “{detail!.name}”? It has never received a lead, so nothing is lost.
            Its {assignments.length} destination{assignments.length === 1 ? '' : 's'} will be left without a client.
          </div>
        )}

        <div className="modal-actions" style={{ justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {mode === 'edit' && (
              <button className="btn btn-secondary" onClick={archive} disabled={saving}>
                {detail!.status === 'archived' ? 'Unarchive' : 'Archive'}
              </button>
            )}
            {mode === 'edit' && !hasDeliveries && !confirmDelete && (
              <button className="btn btn-secondary" onClick={() => setConfirmDelete(true)} disabled={saving}>Delete</button>
            )}
            {mode === 'edit' && !hasDeliveries && confirmDelete && (
              <button className="btn btn-secondary" onClick={remove} disabled={saving} style={{ color: '#b91c1c' }}>
                {saving ? 'Deleting…' : 'Yes, delete'}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : mode === 'create' ? 'Add Client' : 'Save Changes'}
            </button>
          </div>
        </div>

        {mode === 'edit' && hasDeliveries && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
            This client has received leads, so its delivery history is permanent and it can't be deleted. Archive it to take it out of the pickers.
          </div>
        )}
      </div>
    </div>
  );
}
